import type { LucideIcon } from "lucide-react";
import {
  CheckCircle2,
  PlugZap,
  Activity,
  Bell,
  Brain,
  Camera,
  Clock,
  Code2,
  CreditCard,
  Download,
  Flag,
  BarChart3,
  GitBranch,
  FileText,
  HardDrive,
  HeartPulse,
  Inbox,
  Key,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  Mail,
  MessageCircle,
  Package,
  Server,
  Shield,
  ShoppingCart,
  Sparkles,
  Tag,
  Timer,
  Truck,
  UserCircle,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

/** IDs de pestaña — mismos nombres que Booking Hub + hubs de dominio. */
export type AdminTabId =
  | "hub"
  | "dashboard"
  | "negocio"
  | "cuentas"
  | "producto"
  | "comunicaciones"
  | "cobros"
  | "operaciones"
  | "desarrollo"
  | "renata"
  | "tenants"
  | "leads"
  | "invitaciones"
  | "users"
  | "platform-staff"
  | "flags"
  | "analytics-metrics"
  | "cron-registry"
  | "planes"
  | "permisos"
  | "roles"
  | "platform"
  | "push"
  | "releases"
  | "onboarding"
  | "aprobaciones"
  | "compliance"
  | "messaging"
  | "email-templates"
  | "email-gates"
  | "nicho-nav"
  | "nichos-modulos"
  | "saas-mp"
  | "cobros-payway"
  | "cobros-uala"
  | "cobros-transfer"
  | "cobros-self-service"
  | "cobros-routing"
  | "cobros-ops"
  | "billing-admin"
  | "feature-catalog"
  | "packs"
  | "help-analytics"
  | "usage-ops"
  | "support-inbox"
  | "platform-status"
  | "netlify-env";

export type AdminDomainId =
  | "dashboard"
  | "negocio"
  | "cuentas"
  | "producto"
  | "comunicaciones"
  | "cobros"
  | "versiones"
  | "operaciones"
  | "desarrollo"
  | "renata";

export type AdminHubCard = {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  tab?: AdminTabId;
  href?: string;
};

export type AdminHubGroup = {
  title: string;
  cardIds: string[];
};

const TAB_SET = new Set<string>([
  "hub",
  "dashboard", "negocio", "cuentas", "producto", "comunicaciones", "cobros",
  "operaciones", "desarrollo", "renata", "tenants", "leads", "invitaciones",
  "users", "platform-staff", "flags", "analytics-metrics", "cron-registry",
  "planes", "permisos", "roles", "platform", "push", "releases", "onboarding",
  "aprobaciones", "compliance",
  "messaging", "email-templates", "email-gates", "nicho-nav", "nichos-modulos", "saas-mp",
  "cobros-payway", "cobros-uala", "cobros-transfer", "cobros-self-service",
  "cobros-routing", "cobros-ops",
  "billing-admin", "feature-catalog", "packs", "help-analytics", "usage-ops",
  "support-inbox", "platform-status", "netlify-env",
]);

export function isAdminTabId(value: string | undefined | null): value is AdminTabId {
  return Boolean(value && TAB_SET.has(value));
}

/** Sidebar: ~10 dominios. `targetTab` es el tab que se abre al hacer click. */
export const ADMIN_DOMAIN_NAV: {
  id: AdminDomainId;
  label: string;
  icon: LucideIcon;
  targetTab: AdminTabId;
}[] = [
  { id: "dashboard", label: "Panel", icon: LayoutDashboard, targetTab: "dashboard" },
  { id: "negocio", label: "Negocio", icon: Users, targetTab: "negocio" },
  { id: "cuentas", label: "Cuentas y accesos", icon: Shield, targetTab: "cuentas" },
  { id: "producto", label: "Producto", icon: LayoutGrid, targetTab: "producto" },
  { id: "comunicaciones", label: "Comunicaciones", icon: Mail, targetTab: "comunicaciones" },
  { id: "cobros", label: "Cobros", icon: Wallet, targetTab: "cobros" },
  { id: "versiones", label: "Versiones", icon: GitBranch, targetTab: "releases" },
  { id: "operaciones", label: "Operaciones", icon: Server, targetTab: "operaciones" },
  { id: "desarrollo", label: "Desarrollo", icon: Code2, targetTab: "desarrollo" },
  { id: "renata", label: "Renata / IA", icon: Sparkles, targetTab: "renata" },
];

/** Compat: tests/UI que aún iteran el nav principal. */
export const ADMIN_MAIN_NAV = ADMIN_DOMAIN_NAV.map(({ targetTab, label, icon }) => ({
  id: targetTab,
  label,
  icon,
}));

export const ADMIN_PAGE_TITLES: Record<AdminTabId, string> = {
  hub: "Hub de Admin",
  dashboard: "Panel principal",
  negocio: "Negocio",
  cuentas: "Cuentas y accesos",
  producto: "Producto",
  comunicaciones: "Comunicaciones",
  cobros: "Cobros",
  operaciones: "Operaciones",
  desarrollo: "Desarrollo",
  renata: "Renata / IA",
  tenants: "Workspaces",
  leads: "Leads",
  invitaciones: "Invitaciones",
  users: "Usuarios",
  "platform-staff": "Staff de plataforma",
  flags: "Funciones por workspace",
  "analytics-metrics": "Métricas",
  "cron-registry": "Tareas programadas",
  planes: "Planes",
  permisos: "Permisos",
  roles: "Roles",
  platform: "Correo y Resend",
  push: "Avisos y notificaciones",
  releases: "Versiones",
  onboarding: "Onboarding",
  aprobaciones: "Por aprobar",
  compliance: "Arrepentimiento / Baja de servicio",
  messaging: "Mensajería",
  "email-templates": "Plantillas de correo",
  "email-gates": "Reglas de correo",
  "nicho-nav": "Menú por nicho",
  "nichos-modulos": "Módulos por nicho",
  "saas-mp": "Mercado Pago",
  "cobros-payway": "Payway",
  "cobros-uala": "Ualá Bis",
  "cobros-transfer": "Transferencia bancaria",
  "cobros-self-service": "Self-service",
  "cobros-routing": "Routing por operación",
  "cobros-ops": "Operaciones / Diagnóstico",
  "billing-admin": "Suscripciones",
  "feature-catalog": "Catálogo de funciones",
  packs: "Packs",
  "help-analytics": "Ayuda",
  "usage-ops": "Uso",
  "support-inbox": "Soporte",
  "platform-status": "Estado de servicios",
  "netlify-env": "Variables de entorno",
};

export const ADMIN_HUB_TABS: AdminTabId[] = [
  "hub",
  "negocio", "cuentas", "producto", "comunicaciones", "cobros", "operaciones", "desarrollo", "renata",
];

export function isAdminHubTab(tab: AdminTabId): boolean {
  return ADMIN_HUB_TABS.includes(tab);
}

const TAB_TO_DOMAIN: Record<AdminTabId, AdminDomainId> = {
  hub: "dashboard",
  dashboard: "dashboard",
  negocio: "negocio",
  tenants: "negocio",
  leads: "negocio",
  invitaciones: "negocio",
  onboarding: "negocio",
  aprobaciones: "negocio",
  compliance: "negocio",
  cuentas: "cuentas",
  users: "cuentas",
  "platform-staff": "cuentas",
  roles: "cuentas",
  permisos: "cuentas",
  producto: "producto",
  "nicho-nav": "producto",
  "nichos-modulos": "producto",
  flags: "producto",
  planes: "producto",
  "analytics-metrics": "producto",
  "feature-catalog": "producto",
  packs: "producto",
  comunicaciones: "comunicaciones",
  platform: "comunicaciones",
  "email-templates": "comunicaciones",
  "email-gates": "comunicaciones",
  push: "comunicaciones",
  messaging: "operaciones",
  cobros: "cobros",
  "saas-mp": "cobros",
  "cobros-payway": "cobros",
  "cobros-uala": "cobros",
  "cobros-transfer": "cobros",
  "cobros-self-service": "cobros",
  "cobros-routing": "cobros",
  "cobros-ops": "cobros",
  "billing-admin": "cobros",
  releases: "versiones",
  operaciones: "operaciones",
  "cron-registry": "operaciones",
  "platform-status": "operaciones",
  "netlify-env": "operaciones",
  "help-analytics": "operaciones",
  "usage-ops": "operaciones",
  "support-inbox": "operaciones",
  desarrollo: "desarrollo",
  renata: "renata",
};

export function resolveAdminDomain(opts: { tab?: string | null; pathname?: string }): AdminDomainId {
  const path = opts.pathname ?? "";
  if (path.startsWith("/admin/partners")) return "negocio";
  if (path.startsWith("/admin/ops/api-docs")) return "desarrollo";
  if (path.startsWith("/admin/ops/renata") || path.startsWith("/admin/ops/ai") || path.startsWith("/admin/ops/mcp")) return "renata";
  if (path.startsWith("/admin/ops")) return "operaciones";
  if (isAdminTabId(opts.tab ?? undefined)) return TAB_TO_DOMAIN[opts.tab as AdminTabId];
  return "dashboard";
}

export const NEGOCIO_CARDS: AdminHubCard[] = [
  { id: "aprobaciones", title: "Por aprobar", description: "Acreditá transferencias y aprobá pruebas gratis: se crea la cuenta en un clic", icon: CheckCircle2, tab: "aprobaciones" },
  { id: "tenants", title: "Workspaces", description: "Comercios activos y su estado", icon: Users, tab: "tenants" },
  { id: "leads", title: "Leads", description: "Lista con filtros, búsqueda y KPIs — manuales y self-service juntos", icon: UserPlus, tab: "leads" },
  { id: "invitaciones", title: "Invitaciones", description: "Invitar equipos a un workspace", icon: UserPlus, tab: "invitaciones" },
  { id: "onboarding", title: "Altas abandonadas", description: "Gente que empezó /contratar y no dejó sus datos. Las que sí los dejaron están en Leads", icon: ShoppingCart, tab: "onboarding" },
  { id: "compliance", title: "Arrepentimiento / Baja", description: "Solicitudes de la suscripción SaaS del comercio, no de sus clientes", icon: FileText, tab: "compliance" },
  { id: "partners", title: "Partners y referidos", description: "Red comercial y comisiones", icon: Tag, href: "/admin/partners" },
];

export const CUENTAS_CARDS: AdminHubCard[] = [
  { id: "users", title: "Usuarios", description: "Cuentas de todos los workspaces", icon: UserCircle, tab: "users" },
  { id: "platform-staff", title: "Staff de plataforma", description: "Equipo interno de Structura", icon: Shield, tab: "platform-staff" },
  { id: "roles", title: "Roles", description: "Roles del workspace", icon: Tag, tab: "roles" },
  { id: "permisos", title: "Permisos", description: "Qué puede hacer cada rol", icon: KeyRound, tab: "permisos" },
];

export const PRODUCTO_CARDS: AdminHubCard[] = [
  { id: "solution-menu", title: "Menú de Soluciones", description: "Nombres, textos y agrupación comercial del mega-menú público", icon: LayoutGrid, href: "/admin/solution-menu" },
  { id: "nicho-nav", title: "Menú por nicho", description: "Navegación según el tipo de negocio", icon: LayoutGrid, tab: "nicho-nav" },
  { id: "nichos-modulos", title: "Módulos por nicho", description: "Qué módulos de capacidad compone cada nicho", icon: LayoutGrid, tab: "nichos-modulos" },
  { id: "flags", title: "Funciones por workspace", description: "Activar o desactivar capacidades", icon: Flag, tab: "flags" },
  { id: "feature-catalog", title: "Catálogo de funciones", description: "Inventario de lo que ofrece el producto", icon: LayoutGrid, tab: "feature-catalog" },
  { id: "planes", title: "Planes", description: "Catálogo comercial y límites", icon: CreditCard, tab: "planes" },
  { id: "packs", title: "Packs", description: "Paquetes de funciones", icon: Package, tab: "packs" },
  { id: "analytics-metrics", title: "Métricas", description: "Indicadores por workspace", icon: BarChart3, tab: "analytics-metrics" },
];

export const COMUNICACIONES_CARDS: AdminHubCard[] = [
  { id: "platform", title: "Correo y Resend", description: "Envío de correo de la plataforma", icon: Mail, tab: "platform" },
  { id: "email-templates", title: "Plantillas de correo", description: "Textos y diseños de emails", icon: Mail, tab: "email-templates" },
  { id: "email-gates", title: "Reglas de correo", description: "Cuándo se envía cada plantilla", icon: Mail, tab: "email-gates" },
  { id: "push", title: "Avisos y notificaciones", description: "Avisos push y anuncios", icon: Bell, tab: "push" },
];

/** SCRUM-225 — pasarelas reales + self-service + routing + ops. Sin tusfacturas ni pricing de planes. */
export const COBROS_CARDS: AdminHubCard[] = [
  { id: "saas-mp", title: "Mercado Pago", description: "Pasarela para suscripciones y cobros recurrentes", icon: Wallet, tab: "saas-mp" },
  { id: "cobros-payway", title: "Payway", description: "Tarjetas ARS — suscripciones y packs", icon: CreditCard, tab: "cobros-payway" },
  { id: "cobros-uala", title: "Ualá Bis", description: "Checkout ARS para altas y packs", icon: Wallet, tab: "cobros-uala" },
  { id: "cobros-transfer", title: "Transferencia bancaria", description: "Medio de cobro propio (CBU/alias)", icon: CreditCard, tab: "cobros-transfer" },
  { id: "cobros-self-service", title: "Self-service", description: "Kill-switch de /contratar (métodos = Routing)", icon: ShoppingCart, tab: "cobros-self-service" },
  { id: "cobros-routing", title: "Routing", description: "Prioridad por operación y método de pago", icon: GitBranch, tab: "cobros-routing" },
  { id: "cobros-ops", title: "Operaciones / Diagnóstico", description: "Diagnóstico accionable + sesiones de onboarding", icon: Activity, tab: "cobros-ops" },
  { id: "billing-admin", title: "Suscripciones", description: "Estado de cobros de cada workspace", icon: CreditCard, tab: "billing-admin" },
];

export const OPERACIONES_CARDS: AdminHubCard[] = [
  { id: "health", title: "Salud de plataforma", description: "Estado general de Structura", icon: HeartPulse, href: "/admin/ops/health" },
  { id: "infra", title: "Infraestructura", description: "Servicios, cuotas y proveedores", icon: Server, href: "/admin/ops/infra" },
  { id: "cron-registry", title: "Tareas programadas", description: "Activar o ejecutar tareas automáticas", icon: Timer, tab: "cron-registry" },
  { id: "crons", title: "Historial de tareas", description: "Últimas ejecuciones automáticas", icon: Clock, href: "/admin/ops/crons" },
  { id: "backup", title: "Copias de seguridad", description: "Respaldo y restauración", icon: HardDrive, href: "/admin/ops/backup" },
  { id: "logistics", title: "Logística", description: "Envíos y operadores", icon: Truck, href: "/admin/ops/logistics" },
  { id: "events", title: "Eventos", description: "Actividad reciente de la plataforma", icon: Activity, href: "/admin/ops/events" },
  { id: "errors", title: "Errores", description: "Fallos registrados", icon: Activity, href: "/admin/ops/errors" },
  { id: "traces", title: "Trazas", description: "Seguimiento de una solicitud", icon: GitBranch, href: "/admin/ops/traces" },
  { id: "audit", title: "Auditoría", description: "Quién cambió qué, y cuándo", icon: Shield, href: "/admin/ops/audit-log" },
  { id: "snapshots", title: "Snapshots", description: "Copias de configuración", icon: Camera, href: "/admin/ops/snapshots" },
  { id: "export", title: "Exportar datos", description: "Descargar información operativa", icon: Download, href: "/admin/ops/export" },
  { id: "platform-status", title: "Estado de servicios", description: "Integraciones y jobs internos", icon: Server, tab: "platform-status" },
  { id: "messaging", title: "Mensajería", description: "Diagnóstico del proveedor de mensajería (Kapso/Twilio)", icon: MessageCircle, tab: "messaging" },
  { id: "netlify-env", title: "Variables de entorno", description: "Configuración del entorno de deploy", icon: Key, tab: "netlify-env" },
  { id: "usage-ops", title: "Uso", description: "Consumo y actividad de la plataforma", icon: BarChart3, tab: "usage-ops" },
  { id: "help-analytics", title: "Ayuda", description: "Consultas de ayuda en el producto", icon: LifeBuoy, tab: "help-analytics" },
  { id: "support-inbox", title: "Soporte", description: "Bandeja de consultas internas", icon: Inbox, tab: "support-inbox" },
  { id: "legal", title: "Legal y Compliance", description: "Documentos legales, aceptaciones y KYC", icon: FileText, href: "/admin/ops/legal" },
];

export const OPERACIONES_GROUPS: AdminHubGroup[] = [
  { title: "Estado y funcionamiento", cardIds: ["health", "infra", "cron-registry", "crons", "backup", "platform-status", "messaging", "netlify-env"] },
  { title: "Observabilidad", cardIds: ["events", "errors", "traces", "audit", "snapshots"] },
  { title: "Datos y soporte", cardIds: ["export", "usage-ops", "help-analytics", "support-inbox"] },
  { title: "Logística", cardIds: ["logistics"] },
  { title: "Legal y Compliance", cardIds: ["legal"] },
];

export const DESARROLLO_CARDS: AdminHubCard[] = [
  { id: "api-docs", title: "Documentación API", description: "Referencia técnica de la API", icon: Code2, href: "/admin/ops/api-docs" },
];

export const RENATA_CARDS: AdminHubCard[] = [
  { id: "mcp", title: "IA conectada (MCP)", description: "ChatGPT/Claude/Gemini por comercio: créditos, cupos y llamadas", icon: PlugZap, href: "/admin/ops/mcp" },
  { id: "renata-owner", title: "Renata", description: "Uso y costo por workspace", icon: Sparkles, href: "/admin/ops/renata" },
  { id: "ai-providers", title: "Proveedores de IA", description: "Claves y proveedores configurados", icon: Key, href: "/admin/ops/ai/providers" },
  { id: "ai-health", title: "Salud de IA", description: "Estado de los servicios de IA", icon: HeartPulse, href: "/admin/ops/ai/health" },
  { id: "ai-dashboard", title: "Panel de IA", description: "Actividad del centro de control", icon: LayoutDashboard, href: "/admin/ops/ai/dashboard" },
  { id: "ai-memory", title: "Memoria de IA", description: "Memoria persistente de Renata", icon: Brain, href: "/admin/ops/ai/memory" },
];

export const ADMIN_HUBS: Record<
  Exclude<AdminDomainId, "dashboard" | "versiones">,
  { title: string; description: string; cards: AdminHubCard[]; groups?: AdminHubGroup[] }
> = {
  negocio: {
    title: "Negocio",
    description: "Adquirir, activar y administrar workspaces.",
    cards: NEGOCIO_CARDS,
    groups: [
      { title: "Requiere tu acción", cardIds: ["aprobaciones"] },
      { title: "Clientes y oportunidades", cardIds: ["tenants", "leads", "onboarding", "invitaciones"] },
      { title: "Gestión comercial", cardIds: ["compliance", "partners"] },
    ],
  },
  cuentas: {
    title: "Cuentas y accesos",
    description: "Quién entra a la plataforma y con qué permisos.",
    cards: CUENTAS_CARDS,
  },
  producto: {
    title: "Producto",
    description: "Qué vendemos y qué puede usar cada workspace.",
    cards: PRODUCTO_CARDS,
  },
  comunicaciones: {
    title: "Comunicaciones",
    description: "Correo, avisos y mensajes de la plataforma.",
    cards: COMUNICACIONES_CARDS,
  },
  cobros: {
    title: "Cobros",
    description: "Pasarelas, self-service, routing y diagnóstico de cobros SaaS.",
    cards: COBROS_CARDS,
  },
  operaciones: {
    title: "Operaciones",
    description: "Estado y funcionamiento de Structura.",
    cards: OPERACIONES_CARDS,
    groups: OPERACIONES_GROUPS,
  },
  desarrollo: {
    title: "Desarrollo",
    description: "Herramientas técnicas para el equipo.",
    cards: DESARROLLO_CARDS,
  },
  renata: {
    title: "Renata / IA",
    description: "Superficies existentes de Renata y el centro de control de IA.",
    cards: RENATA_CARDS,
  },
};

/** Descripción corta por dominio para las tarjetas del Hub raíz (reusa ADMIN_HUBS donde existe). */
const ADMIN_DOMAIN_DESCRIPTIONS: Record<AdminDomainId, string> = {
  dashboard: "Métricas y KPIs generales de la plataforma.",
  negocio: "Adquirir, activar y administrar workspaces.",
  cuentas: "Quién entra a la plataforma y con qué permisos.",
  producto: "Qué vendemos y qué puede usar cada workspace.",
  comunicaciones: "Correo, avisos y mensajes de la plataforma.",
  cobros: "Pasarelas, self-service, routing y diagnóstico de cobros SaaS.",
  versiones: "Historial de releases y control de versiones.",
  operaciones: "Estado y funcionamiento de Structura.",
  desarrollo: "Herramientas técnicas para el equipo.",
  renata: "Superficies existentes de Renata y el centro de control de IA.",
};

/** Tarjetas del Hub raíz de Admin — un dominio por tarjeta, reemplaza al sidebar como punto de entrada. */
export const ROOT_HUB_CARDS: AdminHubCard[] = ADMIN_DOMAIN_NAV.map(({ id, label, icon, targetTab }) => ({
  id,
  title: label,
  description: ADMIN_DOMAIN_DESCRIPTIONS[id],
  icon,
  tab: targetTab,
}));

export const ADMIN_ROOT_HUB = {
  title: "Hub de Admin",
  description: "Elegí un área para empezar.",
  cards: ROOT_HUB_CARDS,
};

export const DEMO_LABELS: Record<string, string> = {
  "carniceria-structura": "Carnicería Structura",
  "dietetica-structura": "Dietética Structura",
  "rotiseria-structura": "Rotisería Structura",
  "farmacia-structura": "Farmacia Structura",
  "moda-structura": "Moda Structura",
  "bazar-structura": "Bazar Structura",
  "cafe-structura": "Café Structura",
  pistacho: "Dietética Pistacho",
  americanburger: "American Burger",
  "el-cortejo": "Carnicería El Cortejo",
  "el-bodegon-de-san-lorenzo": "Bodegón San Lorenzo",
  petfy: "Petfy",
};

export const PLAN_PRICE_USD: Record<string, number> = {
  starter: 19,
  pro: 49,
  business: 89,
  free_trial: 0,
  custom: 99,
};

export const ADMIN_TAB_IDS = Object.keys(ADMIN_PAGE_TITLES) as AdminTabId[];
